import { ArrowDownTrayIcon, ArrowLeftIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { NormalizedTranscript } from "../Investigations/api";
import InvestigationTranscript from "../Investigations/InvestigationTranscript";
import PdfFileViewer from "../Investigations/viewers/PdfFileViewer";
import PhotoViewer from "../Investigations/viewers/PhotoViewer";
import SpreadsheetViewer from "../Investigations/viewers/SpreadsheetViewer";
import TextFileViewer from "../Investigations/viewers/TextFileViewer";
import { InvestigationTable } from "../../db/schema";
import AudioBar from "../common/AudioBar";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import { AudioType, fileConfigFromLibraryItem, FileSource } from "../Menu/uploadFileConfig";
import { openReportUpload } from "../Menu/uploadLauncher";
import { FILE_KIND_LABELS, FILE_SOURCE_LABELS, filesApi, FilesItem } from "./api";
import { KindIcon, shortDate, StatusChip } from "./fileKinds";

const isPdf = (f: FilesItem) => /\.pdf$/i.test(f.fileName) || f.fileType === "application/pdf";

// A spreadsheet read straight from the stored file: sheets are synthesised into
// the table shape the investigation viewer expects, rows are paged from the API.
const SpreadsheetPane: React.FC<{ source: FileSource; id: string }> = ({ source, id }) => {
  const [tables, setTables] = useState<InvestigationTable[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    filesApi
      .sheets(source, id)
      .then((sheets) => {
        if (cancelled) return;
        const t = sheets.map(
          (sh) =>
            ({
              id: String(sh.index),
              sheet_name: sh.name,
              header_row: sh.header,
              column_roles: {},
              row_count: sh.rowCount,
            }) as unknown as InvestigationTable,
        );
        setTables(t);
        setSelected(t[0]?.id ?? null);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [source, id]);

  const fetchRows = useMemo(
    () => async (tableId: string, offset: number, limit: number) => {
      const r = await filesApi.sheetRows(source, id, Number(tableId), offset, limit);
      return {
        total: r.total,
        rows: r.rows.map((cells, i) => ({
          row_index: offset + i,
          data: Object.fromEntries(r.header.map((h, c) => [h, cells[c] ?? ""])),
        })),
      };
    },
    [source, id],
  );

  if (failed) return <div className="p-6 text-base-content/70">This spreadsheet could not be shown.</div>;
  if (!tables) return <div className="p-6 text-base-content/60">Loading...</div>;
  return <SpreadsheetViewer tables={tables} selectedTableId={selected} onSelectTable={setSelected} fetchRows={fetchRows} />;
};

// What the AI read from an image, shown under it.
const ImageReadout: React.FC<{ source: FileSource; id: string; ready: boolean }> = ({ source, id, ready }) => {
  const [data, setData] = useState<{ description?: string; ocrText?: string } | null>(null);
  useEffect(() => {
    if (!ready) return;
    filesApi.text(source, id).then(setData).catch(() => setData(null));
  }, [source, id, ready]);
  if (!data || (!data.description && !data.ocrText?.trim())) return null;
  return (
    <div className="border-t border-base-300 p-4 text-sm flex flex-col gap-3">
      {data.description && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-base-content/50 mb-1">AI description</div>
          <p className="text-base-content/80 whitespace-pre-wrap">{data.description}</p>
        </div>
      )}
      {data.ocrText?.trim() && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-base-content/50 mb-1">Text in the image</div>
          <p className="text-base-content/80 whitespace-pre-wrap">{data.ocrText}</p>
        </div>
      )}
    </div>
  );
};

export interface FileDetailViewProps {
  source: FileSource;
  id: string;
  onBack: () => void;
  // Used inside a modal (the Fill Form picker): the file is shown the same way,
  // but nothing on screen sends the officer to another page, and `actions`
  // replaces "Create report" with whatever the host offers (e.g. "Use this file").
  embedded?: boolean;
  actions?: (file: FilesItem) => React.ReactNode;
}

// Everything about one file: header, viewer for its kind, transcript, rename and
// transcribe. The /files/:source/:id page and the Fill Form picker both render it.
export const FileDetailView: React.FC<FileDetailViewProps> = ({ source, id, onBack, embedded = false, actions }) => {
  const [file, setFile] = useState<FilesItem | null>(null);
  const [usedIn, setUsedIn] = useState<{ caseId: string; caseNumber: string }[]>([]);
  const [url, setUrl] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<NormalizedTranscript | null>(null);
  const [participants, setParticipants] = useState<{ label: string; name?: string; role: string }[]>([]);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  // For audio saved as "Other": which kind of transcription to run.
  const [transcribeType, setTranscribeType] = useState<AudioType>("narrative");
  const [transcribeError, setTranscribeError] = useState<string | undefined>();
  // Bumped when a transcript finishes so the load effect fetches it.
  const [reloadKey, setReloadKey] = useState(0);
  // An .mp4 is not always video: whether the file has a picture is only known
  // once the browser reads it. null = not checked yet.
  const [hasPicture, setHasPicture] = useState<boolean | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  useEffect(() => {
    if (!source || !id) return;
    let cancelled = false;
    setHasPicture(null);
    (async () => {
      setLoading(true);
      try {
        const detail = await filesApi.get(source, id);
        if (cancelled) return;
        setFile(detail.file);
        setUsedIn(detail.usedIn);
        setTranscribing(detail.file.source === "library" ? ["pending", "uploading", "processing"].includes(detail.file.status) : Boolean(detail.transcribing));
        setTranscribeError(detail.transcribeError);
        const [viewUrl, tr] = await Promise.all([
          filesApi.viewUrl(source, id).catch(() => null),
          detail.file.metadata.transcriptionUrl ? filesApi.transcript(source, id).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setUrl(viewUrl);
        setTranscript(tr?.transcript ?? null);
        setParticipants(tr?.participants ?? []);
      } catch (error: any) {
        if (error?.response?.status === 404) setNotFound(true);
        else toast.error("Failed to load file");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, id, reloadKey]);

  // While a transcription runs, check back until the transcript lands.
  useEffect(() => {
    if (!transcribing || !source || !id) return;
    const t = setInterval(async () => {
      try {
        const d = await filesApi.get(source, id);
        const running = d.file.source === "library" ? ["pending", "uploading", "processing"].includes(d.file.status) : Boolean(d.transcribing);
        if (d.file.metadata.transcriptionUrl) {
          setReloadKey((k) => k + 1);
        } else if (!running) {
          setTranscribing(false);
          setTranscribeError(d.transcribeError || d.file.error || "Transcription did not finish");
        }
      } catch {
        // keep polling
      }
    }, 6000);
    return () => clearInterval(t);
  }, [transcribing, source, id]);

  if (loading) return <div className="flex-1 p-6 text-base-content/60">Loading...</div>;
  if (notFound || !file) {
    return (
      <div className="flex-1 p-6 text-base-content/70">
        File not found.{" "}
        <button type="button" onClick={onBack} className="text-blue-600 dark:text-blue-300 hover:underline">
          {embedded ? "Back" : "Back to Files"}
        </button>
      </div>
    );
  }

  // A "video" with no picture (an audio recording saved as .mp4) is shown with
  // the audio player and transcript, not an empty black video box.
  const shownKind = file.kind === "video" && hasPicture === false ? "audio" : file.kind;

  const startTranscribe = async () => {
    setTranscribeError(undefined);
    try {
      await filesApi.transcribe(file.source, file.id, file.metadata.audioType ? undefined : transcribeType);
      setTranscribing(true);
    } catch (error: any) {
      setTranscribeError(error?.response?.data?.error || "Could not start the transcription");
    }
  };

  const startRename = () => {
    setNameDraft(file.fileName.replace(/\.[^.]+$/, ""));
    setRenaming(true);
  };
  const saveRename = async () => {
    const next = nameDraft.trim();
    setRenaming(false);
    if (!next || next === file.fileName.replace(/\.[^.]+$/, "")) return;
    try {
      setFile(await filesApi.rename(file.id, next));
    } catch {
      toast.error("Could not rename the file");
    }
  };

  const viewer = (() => {
    if (!url) return <div className="p-6 text-base-content/70">Preparing preview...</div>;
    // Still finding out whether this .mp4 has a picture: probe it without showing a player.
    if (file.kind === "video" && hasPicture === null) {
      return (
        <div className="p-6 text-base-content/60">
          Loading...
          <video
            src={url}
            preload="metadata"
            className="hidden"
            onLoadedMetadata={(e) => setHasPicture(e.currentTarget.videoWidth > 0)}
            onError={() => setHasPicture(true)}
          />
        </div>
      );
    }
    if (shownKind === "audio" || shownKind === "video") {
      return (
        <div className="flex flex-col gap-3">
          {shownKind === "video" && (
            <video ref={setVideoEl} src={url} controls className="w-full max-h-[50vh] rounded-lg bg-black" />
          )}
          {transcript ? (
            <div className="h-[60vh]">
              <InvestigationTranscript
                transcript={transcript}
                audioUrl={url}
                participants={participants as any}
                media={shownKind === "video" ? (videoEl ?? undefined) : undefined}
                hideControls={shownKind === "video"}
                scrollKey={`files:${file.source}:${file.id}`}
              />
            </div>
          ) : (
            shownKind === "audio" && <AudioBar src={url} />
          )}
        </div>
      );
    }
    if (file.kind === "image") {
      return (
        <>
          <div className="h-[70vh]">
            <PhotoViewer url={url} alt={file.fileName || "Image"} />
          </div>
          <ImageReadout source={file.source} id={file.id} ready={file.status === "completed"} />
        </>
      );
    }
    if (file.kind === "spreadsheet") {
      return file.status === "completed" ? (
        <SpreadsheetPane source={file.source} id={file.id} />
      ) : (
        <div className="p-6 text-base-content/70">The spreadsheet is still being read.</div>
      );
    }
    if (isPdf(file)) {
      return (
        <div className="h-[70vh]">
          <PdfFileViewer url={url} />
        </div>
      );
    }
    if (/\.(docx?|txt)$/i.test(file.fileName) && file.status === "completed") {
      return (
        <TextFileViewer
          loadPages={() => filesApi.text(file.source, file.id).then((r) => (r.pages || []).map((p) => ({ page: p.page, text: p.text })))}
        />
      );
    }
    return (
      <div className="p-6 text-base-content/70">
        A preview is not available for this file type yet.
        <div className="mt-3">
          <a href={url} target="_blank" rel="noreferrer">
            <Button variant="secondary" size="sm">
              <ArrowDownTrayIcon className="size-4 mr-1" /> Open the original
            </Button>
          </a>
        </div>
      </div>
    );
  })();

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0">
      {/* Same header bar as the Investigations file screen: Back, title with a
          small subline, actions on the right. */}
      <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 flex-wrap">
        <Button variant="secondary" size="sm" onClick={onBack}>
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="min-w-0 flex-1">
          {renaming ? (
            <input
              autoFocus
              value={nameDraft}
              maxLength={150}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRename();
                if (e.key === "Escape") setRenaming(false);
              }}
              className="font-semibold px-2 py-0.5 border border-base-300 rounded-lg bg-base-100 text-base-content w-full max-w-md"
            />
          ) : (
            <h1 className="font-semibold text-base-content truncate flex items-center gap-2">
              <KindIcon kind={shownKind} className="size-4 shrink-0" />
              <span className="truncate">{file.fileName || "Untitled"}</span>
              {file.source === "library" && (
                <button
                  type="button"
                  onClick={startRename}
                  title="Rename"
                  className="text-base-content/40 hover:text-base-content shrink-0"
                >
                  <PencilSquareIcon className="size-4" />
                </button>
              )}
            </h1>
          )}
          <p className="text-xs text-base-content/60 flex flex-wrap items-center gap-x-2">
            <span>{FILE_KIND_LABELS[shownKind]}</span>
            {file.fileSize && <span>· {file.fileSize}</span>}
            <span>· Added {shortDate(file.createdAt)}</span>
            <span>
              · {FILE_SOURCE_LABELS[file.source]}
              {file.parentLabel ? ` · ${file.parentLabel}` : ""}
            </span>
            <StatusChip status={file.status} error={file.error} />
          </p>
        </div>
        {actions ? (
          actions(file)
        ) : file.status === "completed" && (
          <Button variant="primary" size="sm" onClick={() => openReportUpload([fileConfigFromLibraryItem(file)])}>
            Create report
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {/* Same constrained content column as the Investigations tabs */}
        <div className="mx-auto w-full px-6 py-8 max-w-6xl space-y-4">
      {file.metadata.context && (
        <div className="rounded-xl border border-base-300 bg-base-200/50 px-4 py-3 text-sm text-base-content/80">
          <div className="text-[11px] uppercase tracking-wider text-base-content/50 mb-1">Context</div>
          {file.metadata.context}
        </div>
      )}

      {usedIn.length > 0 && (
        <div className="text-sm text-base-content/70 flex flex-wrap items-center gap-2">
          <span>Used in</span>
          {usedIn.map((c) =>
            embedded ? (
              <span
                key={c.caseId}
                className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs font-semibold"
              >
                {c.caseNumber || "Report"}
              </span>
            ) : (
              <Link
                key={c.caseId}
                to={`/cases/${c.caseId}`}
                className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs font-semibold hover:underline"
              >
                {c.caseNumber || "Report"}
              </Link>
            ),
          )}
        </div>
      )}

      <div className="rounded-xl border border-base-300 bg-base-100 overflow-hidden p-0">{viewer}</div>

      {/* No transcript: say so under the player, and offer to make one. */}
      {shownKind === "audio" && !transcript && url && (
        <div className="flex flex-col items-center gap-3 text-center py-2">
          {transcribing ? (
            <>
              <p className="text-sm text-base-content/70">Transcribing this file. This can take a few minutes, and you can leave this page.</p>
              <span className="loading loading-dots loading-sm text-blue-500" />
            </>
          ) : file.status !== "completed" ? (
            <p className="text-sm text-base-content/60">The transcript is still being prepared.</p>
          ) : file.metadata.transcriptionUrl ? (
            // The file has a transcript on record but it could not be fetched
            // (missing from storage, or a network error). Transcribing again is
            // not offered: the server refuses it for a file that already has one.
            <>
              <p className="text-sm text-base-content/60">This file has a transcript, but it could not be loaded.</p>
              <Button variant="secondary" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
                Try again
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-base-content/60">No transcript is available for this file.</p>
              {/* Saved as "Other": it has no type yet, so ask */}
              {!file.metadata.audioType && (
                <div className="flex items-center gap-2 text-sm text-base-content/70">
                  This is a
                  <Dropdown
                    dense
                    value={transcribeType}
                    onChange={(v) => setTranscribeType(v as AudioType)}
                    options={[
                      { value: "narrative", label: "Narrative (your dictation)" },
                      { value: "interaction", label: "Interaction (an interview)" },
                    ]}
                  />
                </div>
              )}
              <Button variant="secondary" size="sm" onClick={startTranscribe}>
                Transcribe this file
              </Button>
              <p className="text-xs text-base-content/50 max-w-md">
                Reports you create from this file will use this transcript instead of transcribing it again.
              </p>
              {transcribeError && <p className="text-xs text-red-600 dark:text-red-400">{transcribeError}</p>}
            </>
          )}
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

// The /files/:source/:id page.
const FileDetail: React.FC = () => {
  const { source, id } = useParams<{ source: FileSource; id: string }>();
  const navigate = useNavigate();
  if (!source || !id) {
    return (
      <div className="flex-1 p-6 text-base-content/70">
        File not found.{" "}
        <Link to="/files" className="text-blue-600 dark:text-blue-300 hover:underline">
          Back to Files
        </Link>
      </div>
    );
  }
  return <FileDetailView source={source} id={id} onBack={() => navigate("/files")} />;
};

export default FileDetail;
